// Fail-proof network helpers — wraps `fetch` with a transparent
// XMLHttpRequest fallback for any network-class failure.
//
// Why this exists
// ---------------
// On production we've repeatedly seen stale Service Workers and
// over-eager browser extensions hijack `fetch` and surface as either
// "Failed to fetch" / "Load failed" / "NetworkError" or the more
// exotic "Request object could not be cloned" error during structured
// cloning. XHR doesn't go through the same code path, so retrying the
// exact same request over XHR succeeds every time — turning the
// rare-but-painful "the analysis just doesn't load" report into a
// non-event for the user.
//
// Genuine HTTP errors (4xx/5xx with a parsed JSON body) still surface
// to the caller via thrown `Error`s, so business-logic failures aren't
// swallowed.
//
// Public API
// ----------
// • `safeFetchJson(url, { token, headers, signal, method, body })`  → Promise<JSON>
// • `safeFetchBlob(url, { token, headers, signal, method, body })`  → Promise<Blob>
//
// `method` defaults to `'GET'`. When set to a write verb (POST/PUT/
// PATCH/DELETE) the `body` is forwarded both through `fetch` and
// through the XHR fallback so write paths get the same Citrix-
// resilience treatment.
//
// Both helpers expect the *full* URL (caller composes API base + path
// itself) so this util has no dependency on env vars.

export function isNetworkLikeError(err) {
  if (!err) return false;
  if (err instanceof TypeError) return true;
  const m = (err.message || '').toLowerCase();
  return (
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror') ||
    m.includes('network error') ||
    m.includes('could not be cloned') ||
    m.includes('cloned')
  );
}

const _buildHeaders = ({ token, headers, body } = {}) => {
  const h = { ...(headers || {}) };
  if (token) h.Authorization = `Bearer ${token}`;
  // Default to JSON when the caller passes a body but no content-type
  // — saves every page from repeating it. Caller can override.
  if (body !== undefined && !Object.keys(h).some(k => k.toLowerCase() === 'content-type')) {
    h['Content-Type'] = 'application/json';
  }
  return h;
};

function _xhrSend(url, { token, headers, responseType, method = 'GET', body }) {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open(method, url, true);
    const all = _buildHeaders({ token, headers, body });
    Object.entries(all).forEach(([k, v]) => x.setRequestHeader(k, v));
    x.responseType = responseType;
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) {
        resolve(x.response);
      } else {
        let detail;
        if (responseType === 'text') {
          try { detail = JSON.parse(x.responseText).detail; } catch { /* ignore */ }
        }
        reject(new Error(detail || `HTTP ${x.status}`));
      }
    };
    x.onerror = () => reject(new Error('Network error'));
    x.send(body !== undefined ? body : null);
  });
}

// Corporate-Citrix networks regularly drop the FIRST request from a
// session (proxy handshake, idle warm-up). One short-delay retry on
// every network-class failure rescues these flows transparently and
// costs at most one extra round trip when something is genuinely down.
const RETRY_DELAY_MS = 800;
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export async function safeFetchJson(url, opts = {}) {
  const { token, headers, signal, method = 'GET', body } = opts;
  const tryOnce = async () => {
    try {
      const r = await fetch(url, {
        method,
        body,
        headers: _buildHeaders({ token, headers, body }),
        signal,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      // Some write endpoints legitimately return an empty body — handle
      // gracefully instead of throwing on JSON.parse('').
      const txt = await r.text();
      return txt ? JSON.parse(txt) : null;
    } catch (e) {
      if (isNetworkLikeError(e)) {
        const txt = await _xhrSend(url, { token, headers, responseType: 'text', method, body });
        if (!txt) return null;
        try { return JSON.parse(txt); } catch { throw new Error('Bad JSON response'); }
      }
      throw e;
    }
  };
  try { return await tryOnce(); }
  catch (e) {
    if (!isNetworkLikeError(e)) throw e;
    await _sleep(RETRY_DELAY_MS);
    return tryOnce();
  }
}

export async function safeFetchBlob(url, opts = {}) {
  const { token, headers, signal, method = 'GET', body } = opts;
  const tryOnce = async () => {
    try {
      const r = await fetch(url, {
        method,
        body,
        headers: _buildHeaders({ token, headers, body }),
        signal,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    } catch (e) {
      if (isNetworkLikeError(e)) {
        return _xhrSend(url, { token, headers, responseType: 'blob', method, body });
      }
      throw e;
    }
  };
  try { return await tryOnce(); }
  catch (e) {
    if (!isNetworkLikeError(e)) throw e;
    await _sleep(RETRY_DELAY_MS);
    return tryOnce();
  }
}
